import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { TranslationProvider } from './i18n';
import { applyStoredTabletShell } from './payment/cardOnly';
import { captureDiagnosticFlag } from './features/payment/wallets/sheetFailure';
import './index.css';

/*
 * Before the first render, so the tablet never flashes a phone-width column on its way to the receipt.
 *
 * Every load after the guest's first arrival has the flag in storage and nothing in the URL to re-read it
 * from - a step navigation, a reload, the return from a hosted payment page - so this is what carries the
 * widening across all of them. The arrival itself is handled where the flag is written; see `cardOnly.ts`.
 */
applyStoredTabletShell();

/*
 * Before the first navigation, because that is what drops the query string.
 *
 * `?debug=1` is only ever on the URL the tester opened, and the wallet failure it exists to explain happens
 * several steps later. Same reason `?cardOnly=true` is written down on arrival rather than read where it is
 * used; see `sheetFailure.ts`.
 */
captureDiagnosticFlag();

/**
 * Guest-app defaults, which differ from an admin portal's.
 *
 * A guest opens this once, pays, and leaves — there is no long session to keep warm, and they are often on
 * restaurant wifi or a weak cell signal. So: retry once rather than the default three (a guest waiting on a
 * check does not want four silent attempts before an error), and do not refetch on window focus, because
 * returning from a payment gateway refocuses the window and a refetch mid-payment is exactly wrong.
 */
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 30_000,
        },
    },
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <TranslationProvider>
                    <App />
                </TranslationProvider>
            </BrowserRouter>
        </QueryClientProvider>
    </React.StrictMode>,
);
